import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const DocTable = ({ firmName }) => {
  const [documents, setDocuments] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [docsPerPage, setDocsPerPage] = useState(10);
  const navigate = useNavigate();

  useEffect(() => {
    if (firmName) {
      fetchDocumentsByFirm(firmName);
    }
  }, [firmName]);

  const fetchDocumentsByFirm = async (firm) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/get-docs-by-firm?firmName=${firm}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      setDocuments(response.data);
    } catch (error) {
      console.error(
        "Error fetching firm documents",
        error.response?.data || error.message
      );
    }
  };

  const handleSoftDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This document will be moved to the recycle bin.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, move it!",
    });

    if (confirm.isConfirmed) {
      try {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/delete-doc/${id}`
        );
        setDocuments(documents.filter((doc) => doc._id !== id));
        Swal.fire("Moved!", "The document is moved to recycle bin.", "success");
      } catch (err) {
        Swal.fire("Error!", "Something went wrong.", "error");
        console.error(err);
      }
    }
  };

  // Pagination logic
  const filteredDocs = documents.filter((doc) =>
    doc.title.toLowerCase().includes(search.toLowerCase())
  );

  const indexOfLast = currentPage * docsPerPage;
  const indexOfFirst = indexOfLast - docsPerPage;
  const currentDocs = filteredDocs.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredDocs.length / docsPerPage);

  return (
    <div className="card shadow-lg p-4 bg-light table-responsive">
      <h2 className="text-primary text-center mb-3">Documents of {firmName}</h2>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <input
          type="text"
          placeholder="Search by Title"
          className="form-control w-50"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1); // Reset to first page when search changes
          }}
        />

        <select
          className="form-select w-auto"
          value={docsPerPage}
          onChange={(e) => {
            setDocsPerPage(Number(e.target.value));
            setCurrentPage(1); // Reset to first page on change
          }}
        >
          {[10, 25, 50, 100].map((val) => (
            <option key={val} value={val}>
              Show {val}
            </option>
          ))}
        </select>
      </div>

      <table className="table table-striped table-hover text-center">
        <thead className="table-dark">
          <tr>
            <th>S.N.</th>
            <th>Title</th>
            <th>Issue Date</th>
            <th>Expiry Date</th>
            <th>Contact Number</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentDocs.map((doc, index) => (
            <tr key={doc._id}>
              <td>{indexOfFirst + index + 1}</td>
              <td>{doc.title}</td>
              <td>{doc.issueDate.split("T")[0]}</td>
              <td>{doc.expiryDate.split("T")[0]}</td>
              <td>{doc.concernPersonPhone}</td>
              <td>
                <a
                  href={`${process.env.REACT_APP_API_URL}/${doc.filePath}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-info fw-bold me-2"
                >
                  Read
                </a>
                <button
                  onClick={() => navigate(`/replace-file/${doc._id}`)}
                  className="btn btn-sm btn-warning fw-bold me-2"
                >
                  Renew
                </button>
                <button
                  onClick={() => handleSoftDelete(doc._id)}
                  className="btn btn-sm btn-danger fw-bold"
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination Controls */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <button
          className="btn btn-outline-primary"
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
        >
          Prev
        </button>

        <div>
          Page{" "}
          <input
            type="number"
            className="form-control d-inline w-25"
            style={{ minWidth: "70px" }}
            value={currentPage}
            onChange={(e) => {
              let page = parseInt(e.target.value);
              if (!isNaN(page) && page >= 1 && page <= totalPages) {
                setCurrentPage(page);
              }
            }}
          />{" "}
          of {totalPages}
        </div>

        <button
          className="btn btn-outline-primary"
          onClick={() =>
            setCurrentPage((prev) => Math.min(prev + 1, totalPages))
          }
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default DocTable;
